import { useState, useEffect } from 'react'
import type { CatalogWatch } from '../../types/watch-monitor'
import { getCatalog, saveCatalog } from '../../lib/watch-monitor-api'
import WatchForm from './WatchForm'

export default function WatchCatalog() {
  const [watches, setWatches] = useState<CatalogWatch[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  useEffect(() => {
    loadCatalog()
  }, [])

  async function loadCatalog() {
    try {
      setLoading(true)
      const data = await getCatalog()
      setWatches(data)
    } catch (err) {
      console.error('Failed to load catalog:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleSave(updatedWatches: CatalogWatch[]) {
    try {
      setSaving(true)
      await saveCatalog(updatedWatches)
      setWatches(updatedWatches)
      setSaveMessage('Catalog saved successfully')
      setTimeout(() => setSaveMessage(null), 3000)
    } catch (err) {
      console.error('Failed to save catalog:', err)
      setSaveMessage('Failed to save catalog')
      setTimeout(() => setSaveMessage(null), 3000)
    } finally {
      setSaving(false)
    }
  }

  function handleAdd(watch: CatalogWatch) {
    const updated = [...watches, watch]
    handleSave(updated)
    setShowAddForm(false)
  }

  function handleEdit(index: number, watch: CatalogWatch) {
    const updated = [...watches]
    updated[index] = watch
    handleSave(updated)
    setEditIndex(null)
  }

  function handleDelete(index: number) {
    if (!confirm(`Remove ${watches[index].brand} ${watches[index].model} ${watches[index].reference} from your catalog?`)) return
    const updated = watches.filter((_, i) => i !== index)
    handleSave(updated)
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 animate-pulse">
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-2" />
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-500 dark:text-slate-400 text-sm">{watches.length} watches in catalog</p>
        <div className="flex items-center gap-3">
          {saveMessage && (
            <span className={`text-sm ${saveMessage.includes('success') ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {saveMessage}
            </span>
          )}
          <button
            onClick={() => { setShowAddForm(true); setEditIndex(null) }}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Watch
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="bg-white dark:bg-slate-800 border border-blue-500/30 rounded-xl p-5 mb-4">
          <h3 className="text-slate-900 dark:text-white font-medium mb-4">Add New Watch</h3>
          <WatchForm
            onSave={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* Watch list */}
      <div className="space-y-3">
        {watches.map((watch, index) => (
          <div key={index} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            {editIndex === index ? (
              <div className="p-5">
                <h3 className="text-slate-900 dark:text-white font-medium mb-4">Edit Watch</h3>
                <WatchForm
                  initialData={watch}
                  onSave={(w) => handleEdit(index, w)}
                  onCancel={() => setEditIndex(null)}
                />
              </div>
            ) : (
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-slate-900 dark:text-white font-medium">{watch.brand} {watch.model}</h3>
                      <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded-full">{watch.reference}</span>
                    </div>
                    {watch.nickname && (
                      <p className="text-slate-500 dark:text-slate-400 text-sm mb-2">"{watch.nickname}"</p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {watch.search_terms.slice(0, 5).map((term) => (
                        <span key={term} className="px-1.5 py-0.5 bg-slate-200/50 dark:bg-slate-700/50 text-slate-500 text-xs rounded">
                          {term}
                        </span>
                      ))}
                      {watch.search_terms.length > 5 && (
                        <span className="text-slate-400 dark:text-slate-600 text-xs">+{watch.search_terms.length - 5} more</span>
                      )}
                    </div>
                    {watch.notes && (
                      <p className="text-slate-500 text-xs">{watch.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => { setEditIndex(index); setShowAddForm(false) }}
                      className="p-1.5 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      disabled={saving}
                      className="p-1.5 text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
